import { useMemo } from "react";
import { useExplorer } from "./ExplorerContext";
import { usePipelineStaging } from "./pipelineStaging";
import { useStudioPipeline } from "./StudioPipelineProvider";
import { lintBlockReason } from "./stagingModel";
import type {
  PipelineDescriptor,
  StagedSnapshot,
  StudioState,
} from "./studioSnapshot";
import { usePipelineCode } from "./useAiHelperProps";

// The Studio as the AI Helper's snapshot and actions see it — one object
// gathered from the three providers.

type Open = ReturnType<typeof useStudioPipeline>["openPipeline"];

function describePipeline(
  open: Open,
  jobState: string | null,
): PipelineDescriptor {
  if (!open) return { kind: "scratch" };
  if (open.kind === "draft") {
    return { kind: "draft", name: open.workspace.name };
  }
  return {
    kind: "deployed",
    name: open.workspace.name,
    state: jobState ?? "unknown",
  };
}

// The staged pipeline, the selection, the Output panel and the last run.
function useStagedSnapshot(): StagedSnapshot {
  const s = usePipelineStaging();
  const { code } = usePipelineCode();
  const run = s.scriptRunOutput;
  const lastRun = useMemo(
    () => (run ? { name: run.scriptName, success: run.success } : null),
    [run],
  );
  const runBlockReason = lintBlockReason(
    s.studioView,
    s.pipelineCodeLintStatus,
  );
  const input = useMemo(
    () => ({ document: s.inputDocument, origin: s.inputOrigin }),
    [s.inputDocument, s.inputOrigin],
  );
  return useMemo(
    () => ({
      sources: s.sources,
      transforms: s.transforms,
      sinks: s.sinks,
      schemas: s.schemas,
      tests: s.tests,
      suiteName: s.stagedSuite?.name ?? null,
      selectedId: s.effectiveSelectedId,
      outputCollapsed: s.outputCollapsed,
      lastRun,
      runBlockReason,
      runningId: s.scriptRunningId,
      input,
      codeEmpty: s.pipelineCodeLintStatus === "empty",
      code: s.studioView === "full" ? code : null,
    }),
    [
      s.sources,
      s.transforms,
      s.sinks,
      s.schemas,
      s.tests,
      s.stagedSuite,
      s.effectiveSelectedId,
      s.outputCollapsed,
      lastRun,
      runBlockReason,
      s.scriptRunningId,
      input,
      s.pipelineCodeLintStatus,
      s.studioView,
      code,
    ],
  );
}

export function useStudioState(): StudioState {
  const explorer = useExplorer();
  const s = usePipelineStaging();
  const { openPipeline, editsLocked, jobControl } = useStudioPipeline();
  const jobState = jobControl?.displayState ?? null;
  const pipeline = useMemo(
    () => describePipeline(openPipeline, jobState),
    [openPipeline, jobState],
  );
  const staged = useStagedSnapshot();
  return useMemo(
    () => ({
      view: s.studioView,
      tab: s.stageTab,
      editsLocked,
      pipeline,
      filter: explorer.filterText,
      expanded: explorer.expanded,
      collapsedSections: explorer.collapsedSections,
      sections: explorer.sections,
      roleOf: s.roleOf,
      sources: s.sources,
      stagedSuite: s.stagedSuite,
      seedingStageIds: s.seedingStageIds,
      staged,
    }),
    [
      s.studioView,
      s.stageTab,
      editsLocked,
      pipeline,
      explorer.filterText,
      explorer.expanded,
      explorer.collapsedSections,
      explorer.sections,
      s.roleOf,
      s.sources,
      s.stagedSuite,
      s.seedingStageIds,
      staged,
    ],
  );
}
